import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { TransferStoreWorkshopDetailModel } from 'src/app/models/maintenance/transfer-store-workshop-detail.model';
import { TransferStoreWorkshopModel } from 'src/app/models/maintenance/transfer-store-workshop.model';
import { RentalStockModel } from 'src/app/models/rental/rental-stock-response.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { StoreService } from 'src/app/services/archive/store.service';
import { TransferStoreWorkshopDetailService } from 'src/app/services/maintenance/transfer-store-workshop-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-transfer-store-workshop-detail',
  templateUrl: './register-transfer-store-workshop-detail.component.html',
  styleUrls: ['./register-transfer-store-workshop-detail.component.scss'],
  standalone: false
})
export class RegisterTransferStoreWorkshopDetailComponent implements OnChanges {

  @Input() transferStoreWorkshopDetailContract: TransferStoreWorkshopDetailModel = new TransferStoreWorkshopDetailModel();
  @Input() transferStoreWorkshopModel: TransferStoreWorkshopModel = new TransferStoreWorkshopModel();
  @Input() transferStoreWorkshopDetails: TransferStoreWorkshopDetailModel[] = [];
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>(false);

  formTransferStoreWorkshopDetail: FormGroup = new FormGroup({});
  transferStoreWorkshopDetailId: number = 0;
  codeNameArticle: string = '';
  codeNameStore: string = '';
  stockArticle: number = 0;

  constructor(private articleService: ArticleService,
    private formBuilder: FormBuilder,
    private transferStoreWorkshopDetailService: TransferStoreWorkshopDetailService,
    private storeService: StoreService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new TransferStoreWorkshopDetailModel());
  }

  async ngOnChanges(changes: SimpleChanges) {
    const transferStoreWorkshopDetails = changes["transferStoreWorkshopDetails"];
    const transferStoreWorkshopDetailContract = changes["transferStoreWorkshopDetailContract"];
    if (transferStoreWorkshopDetails && !transferStoreWorkshopDetails.firstChange) {
      await this.createTransferStoreWorkshopDetails(this.transferStoreWorkshopDetails);
      return;
    }
    if (transferStoreWorkshopDetailContract && !transferStoreWorkshopDetailContract.firstChange) {
      if (this.transferStoreWorkshopDetailContract.transferStoreWorkshopDetailId == 0) {
        this.createNewDetail();
      } else {
        this.transferStoreWorkshopDetailId = this.transferStoreWorkshopDetailContract.transferStoreWorkshopDetailId;
        this.validateForm(this.transferStoreWorkshopDetailContract);
      }
    }
  }

  async configureFormControls(salesRemissionDetail: TransferStoreWorkshopDetailModel) {
    const { storeId, articleId } = salesRemissionDetail;
    if (storeId && articleId) {
      await Promise.all([
        this.getStore(storeId),
        this.getArticle(articleId, storeId)
      ]);
    }
  }

  clearForm() {
    this.codeNameStore = '';
    this.codeNameArticle = '';
    this.transferStoreWorkshopDetailId = 0;
    this.isFormView.emit(false);
    this.validateForm(new TransferStoreWorkshopDetailModel());
  }

  createNewDetail() {
    this.codeNameStore = '';
    this.codeNameArticle = '';
    this.transferStoreWorkshopDetailId = 0;
    this.validateForm(this.transferStoreWorkshopDetailContract);
  }

  async createTransferStoreWorkshopDetails(transferStoreWorkshopDetails: TransferStoreWorkshopDetailModel[]) {
    try {
      await firstValueFrom(this.transferStoreWorkshopDetailService.createSomeDetails(transferStoreWorkshopDetails));
      this.toastHandlerService.showToast('success', 'Los detalles del traslado fueron creados exitosamente');
      this.updateListDetail.emit(true);
    } catch (error) {
      console.error('Error al crear los detalles del traslado.', error);
    }
  }

  async createTransferStoreWorkshopDetail(modelTransferStoreWorkshopDetail: TransferStoreWorkshopDetailModel) {
    try {
      await firstValueFrom(this.transferStoreWorkshopDetailService.createAsync(modelTransferStoreWorkshopDetail));
      this.toastHandlerService.showToast('success', `El detalle del traslado fue creado exitosamente.`);
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al crear el detalle del traslado.', error);
    }
  }

  async getArticle(articleId: number, storeId: number) {
    try {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      this.codeNameArticle = `${article.code} - ${article.name}`;
      await this.getStocksByArticleIdStoreId(articleId, storeId);
    } catch (error) {
      console.error('Error al obtener el articulo por Id.', error);
      this.codeNameArticle = '';
    }
  }

  async getStocksByArticleIdStoreId(articleId: number, storeId: number) {
    const rentalStockModel = new RentalStockModel();
    rentalStockModel.articleId = articleId;
    rentalStockModel.storeId = storeId;
    rentalStockModel.dateMovement = this.transferStoreWorkshopModel.dateTransferStoreWorkshop;
    try {
      const stock = await firstValueFrom(this.transferStoreWorkshopDetailService.getStocksByArticleIdStoreId(rentalStockModel));
      this.stockArticle = stock;
    } catch (error) {
      this.stockArticle = 0;
      console.error('Error al buscar las existencias por artículo y bodega.', error);
    }
  }

  async getStore(storeId: number) {
    try {
      const store = await firstValueFrom(this.storeService.getStoreById(storeId));
      this.codeNameStore = `${store.code} - ${store.name}`;
    } catch (error) {
      console.error('Error al obtener la bodega por Id.', error);
      this.codeNameStore = '';
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formTransferStoreWorkshopDetail.valid) {
      this.showValidationMessagesDetail();
      return;
    }
    const model = this.formTransferStoreWorkshopDetail.getRawValue() as TransferStoreWorkshopDetailModel;
    model.transferStoreWorkshopId = this.transferStoreWorkshopModel.transferStoreWorkshopId;
    if (this.transferStoreWorkshopDetailId == 0) {
      await this.createTransferStoreWorkshopDetail(model);
    } else {
      await this.updateTransferStoreWorkshopDetail(model);
    }
  }

  showValidationMessagesDetail() {
    const controlMessages: Record<string, Record<string, string>> = {
      articleId: {
        required: 'Debe seleccionar un Artículo.',
        min: 'Debe seleccionar un Artículo válido.'
      },
      storeId: {
        required: 'Debe seleccionar una Bodega.',
        min: 'Debe seleccionar una Bodega válida.'
      },
      quantity: {
        required: 'Debe ingresar una Cantidad.',
        min: 'La Cantidad debe ser mayor a 0.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formTransferStoreWorkshopDetail.get(controlName);
      if (control?.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updateTransferStoreWorkshopDetail(modelTransferStoreWorkshopDetail: TransferStoreWorkshopDetailModel) {
    try {
      this.transferStoreWorkshopDetailContract.quantity = modelTransferStoreWorkshopDetail.quantity;
      await firstValueFrom(this.transferStoreWorkshopDetailService.updateAsync(this.transferStoreWorkshopDetailContract));
      this.toastHandlerService.showToast('success', `El detalle del traslado fue modificado exitosamente.`);
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al modificar el detalle del traslado.', error);
    }
  }

  async validateForm(transferStoreWorkshopDetailModel: TransferStoreWorkshopDetailModel) {
    this.formTransferStoreWorkshopDetail = this.formBuilder.group({
      transferStoreWorkshopId: [transferStoreWorkshopDetailModel.transferStoreWorkshopId, [Validators.required, Validators.min(1)]],
      articleId: [transferStoreWorkshopDetailModel.articleId, [Validators.required, Validators.min(1)]],
      storeId: [transferStoreWorkshopDetailModel.storeId, [Validators.required, Validators.min(1)]],
      quantity: [transferStoreWorkshopDetailModel.quantity, [Validators.required, Validators.min(1)]]
    });
    await this.configureFormControls(transferStoreWorkshopDetailModel);
  }

  validateFormControls() {
    Object.keys(this.formTransferStoreWorkshopDetail.controls).forEach((key) => {
      const control = this.formTransferStoreWorkshopDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
